<script setup lang="ts">
import { useFlowEditorContext } from '../../composables/useFlowEditor'

/** Saved flow templates: save the canvas, add or load a template, rename, delete. */
const editor = useFlowEditorContext()
const {
  templateList,
  templateName,
  templateStatus,
  renamingTemplateId,
  renamingTemplateName,
  saveTemplate,
  addTemplateById,
  applyTemplateById,
  deleteTemplateById,
  startRenameTemplate,
  commitRenameTemplate,
} = editor.templates
</script>

<template>
  <div class="flow-settings__panel templates-panel">
    <div class="template-save-row">
      <input
        v-model="templateName"
        type="text"
        class="template-name-input"
        :placeholder="$t('flow.templateNamePlaceholder')"
        @keydown.enter="saveTemplate"
      />
      <button type="button" class="pl-btn pl-btn--primary template-save-btn" @click="saveTemplate">
        {{ $t('flow.saveCurrent') }}
      </button>
    </div>
    <div v-if="templateStatus" class="template-status">{{ templateStatus }}</div>

    <div v-if="templateList.length === 0" class="flow-settings__empty">
      {{ $t('flow.noTemplatesSaved') }}
    </div>
    <ul v-else class="template-list">
      <li v-for="tpl in templateList" :key="tpl.id" class="template-entry">
        <div class="template-entry__info">
          <template v-if="renamingTemplateId === tpl.id">
            <input
              v-model="renamingTemplateName"
              class="template-rename-input"
              type="text"
              @keydown.enter="commitRenameTemplate"
              @keydown.escape="renamingTemplateId = null"
              @blur="commitRenameTemplate"
            />
          </template>
          <template v-else>
            <strong
              class="template-entry__name"
              :title="$t('flow.doubleClickToRename')"
              @dblclick="startRenameTemplate(tpl)"
              >{{ tpl.name }}</strong
            >
            <span class="template-entry__date">{{
              new Date(tpl.savedAt).toLocaleDateString()
            }}</span>
          </template>
        </div>
        <div class="template-entry__actions">
          <button
            type="button"
            class="pl-btn pl-btn--secondary template-action-btn"
            :title="$t('flow.addToCanvas')"
            @click="addTemplateById(tpl.id)"
          >
            {{ $t('flow.templateAdd') }}
          </button>
          <button
            type="button"
            class="pl-btn pl-btn--secondary template-action-btn"
            @click="applyTemplateById(tpl.id)"
          >
            {{ $t('flow.templateLoad') }}
          </button>
          <button
            type="button"
            class="pl-btn pl-btn--danger template-action-btn"
            @click="deleteTemplateById(tpl.id)"
          >
            ×
          </button>
        </div>
      </li>
    </ul>
  </div>
</template>

<style scoped src="../styles/inspectorForm.css"></style>

<style scoped>
/* ── Template panel ─────────────────────────────────────────── */
.templates-panel {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.template-save-row {
  display: flex;
  gap: 0.4rem;
  align-items: stretch;
}

.template-name-input {
  flex: 1;
  min-width: 0;
  padding: 0.3rem 0.5rem;
  border: 1px solid #cbd5e1;
  border-radius: 0.45rem;
  font-size: 0.84rem;
}

.template-save-btn {
  flex: 0 0 auto;
  white-space: nowrap;
}

.template-status {
  font-size: 0.78rem;
  color: #4338ca;
  font-style: italic;
}

.template-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.template-entry {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  padding: 0.45rem 0.55rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
}

.template-entry__info {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  min-width: 0;
}

.template-entry__name {
  font-size: 0.83rem;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: text;
}

.template-rename-input {
  width: 100%;
  font-size: 0.83rem;
  font-weight: 600;
  border: 1px solid #a5b4fc;
  border-radius: 0.3rem;
  padding: 0.15rem 0.3rem;
  outline: none;
  box-shadow: 0 0 0 2px rgba(67, 56, 202, 0.2);
}

.template-entry__date {
  font-size: 0.7rem;
  color: #64748b;
}

.template-entry__actions {
  display: flex;
  gap: 0.3rem;
  flex: 0 0 auto;
}

.template-action-btn {
  padding: 0.2rem 0.5rem;
  font-size: 0.76rem;
}
</style>
